import { useEffect, useState } from 'react';
import { FaCheck, FaExclamation, FaQuestion, FaTimes } from 'react-icons/fa';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  Box,
} from '@mui/material';

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | 'xs';

interface Props {
  open: boolean;
  onClose: () => void | undefined;
  title: string;
  description?: string;
  confirmText?: string;
  onConfirm: () => void | undefined;
  cancelText?: string;
  focusOnCancel?: boolean;
  disableBackdropClick?: boolean;
  hideCancel?: boolean;
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullScreen?: boolean;
  fullScreenBreakpoint?: Breakpoint;
  scroll?: 'body' | 'paper';
  variant: 'info' | 'success' | 'warning' | 'error';
  color: 'info' | 'success' | 'warning' | 'error' | 'primary' | 'secondary';
}

export default function IconDialog(props: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(props.fullScreenBreakpoint ?? 'sm'));
  const [isLoading, setIsLoading] = useState(false);

  const onClick = () => {
    setIsLoading(true);
    props.onConfirm();
  };

  useEffect(() => {
    setIsLoading(false);
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={(event, reason) => {
        if (
          (reason === 'backdropClick' && !props.disableBackdropClick) ||
          !props.disableBackdropClick
        ) {
          props.onClose();
        }
      }}
      aria-labelledby="icon-dialog-title"
      aria-describedby="icon-dialog-description"
      fullWidth={props.fullWidth}
      maxWidth={props.maxWidth}
      fullScreen={props.fullScreenBreakpoint ? fullScreen : props.fullScreen}
      scroll={props.scroll}
      sx={{ textAlign: 'center' }}
    >
      <Box style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
        <Avatar
          sx={{
            bgcolor: props.color ? `${props.color}.main` : `${props.variant}.main`,
            width: 70,
            height: 70,
          }}
        >
          {props.variant === 'info' ? (
            <FaQuestion size={50} />
          ) : props.variant === 'success' ? (
            <FaCheck size={50} />
          ) : props.variant === 'warning' ? (
            <FaExclamation size={50} />
          ) : props.variant === 'error' ? (
            <FaTimes size={50} />
          ) : null}
        </Avatar>
      </Box>
      <DialogTitle id="icon-dialog-title">{props.title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="icon-dialog-description">{props.description}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
        {!props.hideCancel ? (
          <Button
            onClick={props.onClose}
            color="inherit"
            autoFocus={props.scroll === 'body' ? false : props.focusOnCancel}
          >
            {props.cancelText ?? 'Cancelar'}
          </Button>
        ) : null}
        <LoadingButton
          loading={isLoading}
          onClick={onClick}
          color="primary"
          autoFocus={props.scroll === 'body' ? false : !props.focusOnCancel}
        >
          {props.confirmText ?? 'Confirmar'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
